import { useEffect, useRef } from 'react'
import axios from 'axios'

/**
 * ダッシュボードのスパークラインを実データで描画する。
 *
 * 以前は public/js/dashboard.js が Math.random() で線を引いていた。
 * この hook は API から直近14日ぶんの USD 建て価格を取得して差し替える。
 * Chart.js は public/index.html で CDN から読み込まれ、window.Chart に入っている。
 */

const CHART_API = 'https://auth.t-bitin.io/api/getchartrate'

/** canvas の id と API のキーの対応。画面の並び順に合わせてある。 */
const CHART_MAP: { [id: string]: string } = {
    test1: 'usdeth', // Ethereum
    test2: 'usdbit', // Axa Coin
    test11: 'usdbic', // BIC
    test3: 'usdusdt', // Tether（1.00 固定）
    test12: 'usdspt', // Sportia
    test13: 'usdvai', // Virtual AI
    test4: 'usdwbtc', // Wrapped Bitcoin
    test5: 'usdsand', // SAND
    test6: 'usdmatic', // Polygon
    test7: 'usduni', // Uniswap
    test8: 'usdmkr', // Maker
    test9: 'usdgrt', // The Graph
    test10: 'usdftm' // Fantom Token
}

const LINE_COLOR = '#00BC9E'

type Series = { [key: string]: (number | null)[] }

function buildConfig(labels: string[], data: (number | null)[]) {
    return {
        type: 'line',
        data: {
            labels,
            datasets: [
                {
                    data,
                    borderColor: LINE_COLOR,
                    borderWidth: 2,
                    // 記録の無い日は null で来る。線を途切れさせずに繋ぐ。
                    spanGaps: true,
                    tension: 0.3,
                    fill: false
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
                x: {
                    grid: { drawBorder: false, display: false },
                    ticks: { display: false }
                },
                y: {
                    // 値域は銘柄ごとに大きく違うので自動に任せる。
                    grid: { drawBorder: false, display: false },
                    ticks: { display: false }
                }
            },
            elements: {
                point: { radius: 0, hitRadius: 8 }
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    displayColors: false,
                    callbacks: {
                        label: (context: any) => {
                            const value = context.parsed?.y

                            if (value === null || value === undefined) {
                                return ''
                            }

                            return 'USD ' + Number(value).toPrecision(6)
                        }
                    }
                }
            }
        }
    }
}

export function useRateCharts(deps: any[] = []) {
    const charts = useRef<any[]>([])

    useEffect(() => {
        let cancelled = false

        const destroyAll = () => {
            charts.current.forEach(chart => {
                try {
                    chart.destroy()
                } catch (e) {
                    // すでに破棄済みなら無視する
                }
            })
            charts.current = []
        }

        const draw = (labels: string[], series: Series) => {
            const ChartJs = (window as any).Chart

            if (!ChartJs) {
                return
            }

            // 同じ canvas に二重に描くと Chart.js が例外を投げるので先に破棄する。
            destroyAll()

            Object.keys(CHART_MAP).forEach(id => {
                const el = document.getElementById(id)

                if (!el) {
                    return
                }

                const data = series[CHART_MAP[id]]

                // 値が1つも無い銘柄は描かない。空の枠のままにする。
                if (!data || data.every(value => value === null || value === undefined)) {
                    return
                }

                charts.current.push(new ChartJs(el, buildConfig(labels, data)))
            })
        }

        axios
            .get(CHART_API)
            .then(res => {
                if (cancelled) {
                    return
                }

                draw(res.data['labels'] || [], res.data)
            })
            .catch(() => {
                // 取得できなければ描かない。数値表示側は別 API なので影響しない。
            })

        return () => {
            cancelled = true
            destroyAll()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps)
}
